import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { routeTree } from './routeTree.gen';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // gcTime bumped so persisted cache entries stay around long enough to
      // be useful; default 5min would drop them before the persister rehydrates
      // them on next mount. 24h matches the persister's maxAge.
      gcTime: 24 * 60 * 60 * 1000,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Persist the React Query cache to localStorage so forms paint immediately
// from last-known-good state on refresh (no skeleton flash). Bumped buster
// invalidates all persisted state when the cache shape changes. In-flight
// edits are persisted server-side via the section Save endpoints, so no
// client-side draft layer is needed on top of this.
const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'nighttax:queries:v1',
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function bootstrap() {
  // MSW is opt-in: only intercept network calls when VITE_USE_MSW=true. With Aspire
  // running the real API, MSW would shadow it (and worker.start() can hang on stale
  // service-worker registrations, leaving the page on a blank screen).
  if (import.meta.env.DEV && import.meta.env.VITE_USE_MSW === 'true') {
    const { worker } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
  }

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Root element missing');

  createRoot(rootEl).render(
    <StrictMode>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: 24 * 60 * 60 * 1000,
          // Strip auth/session-y queries from persisted state — those should
          // always re-fetch fresh; persisting them risks surfacing a stale
          // identity. Adjust the prefix list as new auth-y query keys land.
          dehydrateOptions: {
            shouldDehydrateQuery: (q) => {
              // Only persist completed queries. A query still in-flight when the
              // tab unloads is cancelled by react-query; persisting it as `pending`
              // makes the next session re-attach to a dead promise that
              // immediately rejects with CancelledError ("A query that was
              // dehydrated as pending ended up rejecting"). React-query's default
              // predicate already excludes non-success states; this override has
              // to preserve that or the warning re-appears.
              if (q.state.status !== 'success') return false;

              const first = q.queryKey[0];
              if (typeof first !== 'string') return true;
              // Strip auth/session-y queries from persisted state — those should
              // always re-fetch fresh; persisting them risks surfacing a stale
              // identity. Adjust the prefix list as new auth-y query keys land.
              return !['auth', 'session', 'me'].includes(first);
            },
          },
        }}
      >
        <RouterProvider router={router} />
      </PersistQueryClientProvider>
    </StrictMode>,
  );
}

bootstrap();
